import type { EmploymentTemplateSalary } from "services/api/company/staff.service";

const AGE_SALARY_REQUIREMENT_TYPE = "Age";
const EXPERIENCE_SALARY_REQUIREMENT_TYPE = "Experience";

/**
 * Returns the template value unless it is unset (null/empty/"Undefined"), in
 * which case the fallback is used. Backend enums serialize their unset member
 * as "Undefined", which the dropdowns have no option for.
 */
export const toTemplateEnumOrFallback = <TFallback extends string | undefined>(
  value: string | null,
  fallback: TFallback,
): string | TFallback => {
  if (value == null || value.length === 0 || value === "Undefined") {
    return fallback;
  }
  return value;
};

/**
 * Filters template salaries by the worker's age:
 * - `Age` salaries are kept only when they match the worker's age exactly.
 * - When the worker matches at least one `Age` requirement, `Experience`
 *   salaries are hidden (age brackets are low and experience requirements high,
 *   so they don't apply to someone selected by a youth age bracket).
 * - Salaries with no age/experience requirement always pass.
 * - When the worker's age is unknown, all salaries are kept.
 */
export const filterTemplateSalariesByAge = (
  salaries: EmploymentTemplateSalary[],
  workerAge: number | null,
): EmploymentTemplateSalary[] => {
  if (workerAge == null) return salaries;

  const matchesAnyAgeRequirement = salaries.some(
    (salary) =>
      salary.requirementType === AGE_SALARY_REQUIREMENT_TYPE &&
      salary.requirement === workerAge,
  );

  return salaries.filter((salary) => {
    if (salary.requirementType === AGE_SALARY_REQUIREMENT_TYPE) {
      return salary.requirement === workerAge;
    }
    if (salary.requirementType === EXPERIENCE_SALARY_REQUIREMENT_TYPE) {
      return !matchesAnyAgeRequirement;
    }
    return true;
  });
};
